import { useState } from 'react'
import logger from '@/utils/logger'
import {
  useAdminSchemaTablesGraphQL,
  useAdminSchemaTableColumnsGraphQL,
  type SchemaTable,
  type SchemaColumn,
} from '@/hooks/graphql/useAdminQueryGraphQL'

interface SchemaExplorerProps {
  onTableSelect: (tableName: string) => void
}

/**
 * Table Tree Item - Expandable table with columns
 */
function TableTreeItem({
  table,
  isExpanded,
  onToggle,
  onSelect,
}: {
  table: SchemaTable
  isExpanded: boolean
  onToggle: () => void
  onSelect: () => void
}) {
  const { columns, loading, error } = useAdminSchemaTableColumnsGraphQL(
    isExpanded ? table.name : null
  )

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggle()
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect()
  }

  return (
    <div>
      {/* Table row */}
      <button
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className="w-full text-left px-3 py-2 rounded-md hover:bg-surface-2 transition-colors group"
        title="Click to expand, double-click to query"
      >
        <div className="flex items-center gap-2">
          {/* Expand/collapse chevron */}
          <span
            className={`text-muted transition-transform duration-150 ${
              isExpanded ? 'rotate-90' : ''
            }`}
          >
            ▶
          </span>
          <span className="text-lg">📊</span>
          <span className="font-mono text-sm text-foreground group-hover:text-primary transition-colors flex-1">
            {table.name}
          </span>
          <span className="text-xs bg-surface-2 px-2 py-0.5 rounded text-muted">
            {table.columnCount}
          </span>
        </div>
      </button>

      {/* Columns list (when expanded) */}
      {isExpanded && (
        <div className="ml-6 pl-4 border-l border-border">
          {loading && (
            <div className="py-2 text-xs text-muted">Loading columns...</div>
          )}
          {error && (
            <div className="py-2 text-xs text-danger">Error: {error}</div>
          )}
          {!loading && !error && columns.length === 0 && (
            <div className="py-2 text-xs text-muted">No columns found</div>
          )}
          {!loading && !error && columns.length > 0 && (
            <div className="py-1 space-y-0.5">
              {columns.map((column) => (
                <ColumnItem key={column.name} column={column} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Column Item - Displays column info
 */
function ColumnItem({ column }: { column: SchemaColumn }) {
  return (
    <div className="flex items-center gap-2 py-1 px-2 text-xs rounded hover:bg-surface-1 group">
      {/* Primary key indicator */}
      {column.isPrimaryKey ? (
        <span className="text-amber-500" title="Primary Key">
          🔑
        </span>
      ) : (
        <span className="text-muted">○</span>
      )}
      {/* Column name */}
      <span className="font-mono text-foreground">{column.name}</span>
      {/* Data type */}
      <span className="text-muted flex-1">{column.dataType}</span>
      {/* Nullable indicator */}
      {!column.isNullable && (
        <span className="text-xs text-muted bg-surface-2 px-1 rounded" title="NOT NULL">
          NN
        </span>
      )}
    </div>
  )
}

/**
 * Schema Explorer - Database table browser with tree view
 *
 * Displays a list of database tables that can be expanded to show columns.
 * Click to expand/collapse, double-click to run a SELECT query.
 *
 * Feature: 055-db-console
 */
export function SchemaExplorer({ onTableSelect }: SchemaExplorerProps) {
  const { tables, loading, error } = useAdminSchemaTablesGraphQL()
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set())

  const handleToggle = (tableName: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev)
      if (next.has(tableName)) {
        next.delete(tableName)
      } else {
        next.add(tableName)
      }
      return next
    })
  }

  const handleTableSelect = (tableName: string) => {
    logger.info('SchemaExplorer', 'Table selected', { tableName })
    onTableSelect(tableName)
  }

  // Filter tables by search term (matches table name OR any column name)
  const searchLower = searchTerm.toLowerCase()
  const filteredTables = tables.filter((table) => {
    // Match table name
    if (table.name.toLowerCase().includes(searchLower)) {
      return true
    }
    // Match any column name
    return table.columnNames.some((col) => col.toLowerCase().includes(searchLower))
  })

  return (
    <div className="h-full flex flex-col bg-surface-1 border-r border-border">
      <div className="p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Schema Explorer</h2>
        <p className="text-sm text-muted">Database Tables</p>
      </div>

      {/* Search Input */}
      <div className="p-4 border-b border-border">
        <input
          type="text"
          placeholder="Search tables & columns..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border text-foreground placeholder:text-muted rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
        />
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted">Loading tables...</div>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-sm text-red-600">
            <strong>Error:</strong> {error}
          </div>
        )}

        {!loading && !error && tables.length === 0 && (
          <div className="text-sm text-muted text-center py-8">
            No tables found
          </div>
        )}

        {!loading && !error && tables.length > 0 && filteredTables.length === 0 && (
          <div className="text-sm text-muted text-center py-8">
            No tables match &quot;{searchTerm}&quot;
          </div>
        )}

        {!loading && !error && filteredTables.length > 0 && (
          <div className="space-y-0.5">
            {filteredTables.map((table) => (
              <TableTreeItem
                key={table.name}
                table={table}
                isExpanded={expandedTables.has(table.name)}
                onToggle={() => handleToggle(table.name)}
                onSelect={() => handleTableSelect(table.name)}
              />
            ))}
          </div>
        )}
      </div>

      {!loading && !error && (
        <div className="p-3 border-t border-border">
          <div className="text-xs text-muted text-center">
            {filteredTables.length} of {tables.length} table{tables.length !== 1 ? 's' : ''}
            {searchTerm && ' (filtered)'}
          </div>
          <div className="text-xs text-muted text-center mt-1">
            Click to expand • Double-click to query
          </div>
        </div>
      )}
    </div>
  )
}
